import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray, transformerObject } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';

type DishSliceState = {
  loading: boolean;
  listDishSalt: DishSalt[];
  listDishAge: DishAge[];
  listDishCookingMethod: DishCookingMethod[];
  listSourceDish: ListSourceDish[];
  listSizeDish: SizeDish[];
  listRegionDish: RegionDish[];
  listTagDish: TagDish[];
  listDish: Dish[];
  listDishById?: DetailDish;
  listHistoryDish?: HistoryDish[];
  listStructureMealDish: StructureMealDish[];
  message?: MessageType;
  pagination: Pagination;
  paginationHistory: Pagination;
  fileUrl: string;
  downloadTemplateQuantitative?: TemplateDish;
};

const initialState: DishSliceState = {
  loading: false,
  listDishSalt: [],
  listDishAge: [],
  listDishCookingMethod: [],
  listSourceDish: [],
  listSizeDish: [],
  listRegionDish: [],
  listTagDish: [],
  listDish: [],
  listStructureMealDish: [],
  pagination: PAGINATION,
  paginationHistory: PAGINATION,
  fileUrl: '',
  downloadTemplateQuantitative: undefined
};

export const dishSlice = createAppSlice({
  name: 'dish',
  initialState,
  reducers: (create) => ({
    getListDishSalt: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          'dish-management/dish-salt-formula/list'
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listDishSalt = transformerArray(action.payload[0]);
        },
        rejected: (state) => {
          state.loading = false;
          state.listDishSalt = [];
        }
      }
    ),
    getListDishAge: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-age-range/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listDishAge = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listDishAge = [];
        }
      }
    ),
    getListDishCookingMethod: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-cooking-method/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listDishCookingMethod = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listDishCookingMethod = [];
        }
      }
    ),
    getListSourceDish: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-external-type/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listSourceDish = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listSourceDish = [];
        }
      }
    ),
    getListStructureMealDish: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-meal-structure/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listStructureMealDish = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listStructureMealDish = [];
        }
      }
    ),
    getListSizeDish: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-size-type/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listSizeDish = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listSizeDish = [];
        }
      }
    ),
    getListDishRegion: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          'dish-management/dish-region/list'
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listRegionDish = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listRegionDish = [];
        }
      }
    ),
    getListDishTag: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('dish-management/dish-tag/list');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listTagDish = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listTagDish = [];
        }
      }
    ),
    getListDish: create.asyncThunk(
      async ({
        query,
        offset,
        limit
      }: {
        query?: FilterProduct;
        offset?: number;
        limit?: number;
      }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('page', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `dish-management/list${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listDish = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listDish = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    getDetailDish: create.asyncThunk(
      async ({
        id,
        ageGroupId,
        dishSizeId
      }: {
        id: string;
        ageGroupId?: string;
        dishSizeId?: string;
      }) => {
        const response = await apiFetcher.get(
          `dish-management/${id}?ageGroupId=${ageGroupId}&id=${id}&dishSizeId=${dishSizeId}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.listDishById = initialState.listDishById;
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          const detailData = action.payload.detail;
          const listSize = action.payload.listSize;
          state.listDishById = transformerObject({
            ...detailData,
            listSize: listSize
          });
        },
        rejected: (state) => {
          state.loading = false;
          state.listDishById = undefined;
        }
      }
    ),
    createDish: create.asyncThunk(
      async (query: RecordDish | undefined, { rejectWithValue }) => {
        try {
          const response = await apiFetcher.post('dish-management', query, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          return response;
        } catch (err: any) {
          const message = err?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_DISH_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          let errorMsg = MESSAGE.CREATE_DISH_FAIL;
          if (typeof action.payload === 'string') {
            errorMsg = action.payload;
          }
          state.message = {
            type: Message.ERROR,
            message: errorMsg
          };
        }
      }
    ),
    editDish: create.asyncThunk(
      async ({
        id,
        payload,
        ageGroupId
      }: {
        id: string;
        payload: Record<string, any>;
        ageGroupId: string;
      }) => {
        const response = await apiFetcher.post(
          `dish-management/update?id=${id}&ageGroupId=${ageGroupId}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_DISH_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_DISH_FAIL
          };
        }
      }
    ),
    deleteDish: create.asyncThunk(
      async ({ dishId, formulaId }: { dishId: number; formulaId: string }) => {
        await apiFetcher.post(
          `dish-management/delete/${dishId}?formulaId=${formulaId}`
        );
        return dishId;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_DISH_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_DISH_FAIL
          };
        }
      }
    ),
    importFile: create.asyncThunk(
      async (formData: FormData) => {
        try {
          const response = await apiFetcher.post(
            'dish-management/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error: any) {
          throw new Error(
            error?.response?.data?.message || error?.data?.message
          );
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_DISH_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.IMPORT_DISH_FAIL
          };
        }
      }
    ),
    exportData: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.post(
          'dish-management/export',
          query,
          {
            responseType: 'blob'
          }
        );
        return response;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.fileUrl = action.payload;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.FETCH_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.FETCH_FAIL
          };
        }
      }
    ),
    uploadImage: create.asyncThunk(
      async (payload: UploadImagePayload) => {
        const response = await apiFetcher.post('upload/images', payload);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
        },
        rejected: (state) => {
          state.loading = false;
        }
      }
    ),
    getHistoryDish: create.asyncThunk(
      async ({ offset, limit }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('page', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `dish-management-import/history${params.toString() ? `?${params.toString()}` : ''}`;

        const response = await apiFetcher.get(url);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistoryDish = transformerArray(action.payload.listData);
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistoryDish = [];
          state.paginationHistory = PAGINATION;
        }
      }
    ),
    downloadTemplateQuantitative: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('template/info?type=DISH');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateQuantitative = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateQuantitative = undefined;
        }
      }
    ),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),
  selectors: {
    watchListDishSalt: (dish) => dish.listDishSalt,
    watchListDishAge: (dish) => dish.listDishAge,
    watchListDishCookingMethod: (dish) => dish.listDishCookingMethod,
    watchListSourceDish: (dish) => dish.listSourceDish,
    watchListStructureMealDish: (dish) => dish.listStructureMealDish,
    watchListSizeDish: (dish) => dish.listSizeDish,
    watchListRegionDish: (dish) => dish.listRegionDish,
    watchListTagDish: (dish) => dish.listTagDish,
    watchListDish: (dish) => dish.listDish,
    watchListHistory: (product) => product.listHistoryDish,
    watchDetailDishById: (dish) => dish.listDishById,
    watchDishMessage: (dish) => dish.message,
    watchDishPagination: (dish) => dish.pagination,
    watchDishPaginationHistory: (dish) => dish.paginationHistory,
    watchDishLoading: (dish) => dish.loading,
    watchDownloadTemplateQuantitative: (dish) =>
      dish.downloadTemplateQuantitative
  }
});

export const {
  watchListDishSalt,
  watchListDishAge,
  watchListDishCookingMethod,
  watchListSourceDish,
  watchListStructureMealDish,
  watchListSizeDish,
  watchListRegionDish,
  watchListTagDish,
  watchListDish,
  watchListHistory,
  watchDetailDishById,
  watchDishMessage,
  watchDishPagination,
  watchDishPaginationHistory,
  watchDishLoading,
  watchDownloadTemplateQuantitative
} = dishSlice.selectors;

export const {
  getListDishSalt,
  getListDishAge,
  getListDishCookingMethod,
  getListSourceDish,
  getListSizeDish,
  getListDish,
  getDetailDish,
  createDish,
  editDish,
  deleteDish,
  importFile,
  exportData,
  getListStructureMealDish,
  getListDishRegion,
  getListDishTag,
  getHistoryDish,
  uploadImage,
  resetMessages,
  downloadTemplateQuantitative
} = dishSlice.actions;
